import React, { useState, useEffect } from 'react';
import api, { extractApiError } from '../../services/api';
import toast from 'react-hot-toast';


const SERVICOS_ADICIONAIS = [
  { cod: '001', label: 'Aviso de Recebimento (AR)' },
  { cod: '002', label: 'Mão Própria' },
  { cod: '019', label: 'Valor Declarado (SEDEX)' },
  { cod: '064', label: 'Valor Declarado (PAC)' },
];

// Limites de dimensão por produto (código Correios)
const LIMITES_PRODUTO = {
  '04227': { nome: 'Mini Envios', peso: '300g', comprimento: '16–30cm', largura: '11–21cm', altura: '2–9cm' },
};

function enriquecerErro(erro, codigoServico) {
  if (!erro || !erro.includes('ERP-008')) return erro;
  const limites = LIMITES_PRODUTO[codigoServico];
  if (!limites) return erro;
  return `${erro} | Limites ${limites.nome}: peso até ${limites.peso}, comprimento ${limites.comprimento}, largura ${limites.largura}, altura ${limites.altura}.`;
}

const CARRIER_ENDPOINTS = {
  correios: '/correios/admin/quote',
  jadlog: '/jadlog/admin/quote',
  loggi: '/loggi/admin/quote',
  'jt-express': '/jt-express/admin/quote',
  'distancia-mapbox': '/distancia-mapbox/admin/quote',
};

// Transportadoras que não usam o CEP de origem informado no formulário
// (origem fixa, cadastrada em Fretes)
const ORIGEM_FIXA = ['distancia-mapbox'];

const INITIAL_FORM = {
  cepOrigem: '',
  cepDestino: '',
  peso: '',
  comprimento: '',
  largura: '',
  altura: '',
  vlDeclarado: '',
  servicosAdicionais: [],
};

export default function QuotePage() {
  const [carriers, setCarriers] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [resultado, setResultado] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingCarriers, setLoadingCarriers] = useState(true);
  const [errors, setErrors] = useState({});
  const [debugOpen, setDebugOpen] = useState(false);
  const [integrationOpen, setIntegrationOpen] = useState(false);

  useEffect(() => {
    api.get('/freight/carriers/enabled')
      .then(({ data }) => {
        const list = data.data?.carriers || data.data || [];
        setCarriers(list);
        if (list.length > 0) setSelectedSlug(list[0].slug);
      })
      .catch(() => toast.error('Erro ao carregar transportadoras'))
      .finally(() => setLoadingCarriers(false));
  }, []);

  const selected = carriers.find((c) => c.slug === selectedSlug);
  const endpoint = selectedSlug ? CARRIER_ENDPOINTS[selectedSlug] : null;

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  }

  function toggleServico(cod) {
    setForm((f) => ({
      ...f,
      servicosAdicionais: f.servicosAdicionais.includes(cod)
        ? f.servicosAdicionais.filter((c) => c !== cod)
        : [...f.servicosAdicionais, cod],
    }));
  }

  function validate() {
    const errs = {};
    const cepRe = /^\d{5}-?\d{3}$/;
    if (!ORIGEM_FIXA.includes(selectedSlug) && !cepRe.test(form.cepOrigem)) errs.cepOrigem = 'CEP inválido (00000-000)';
    if (!cepRe.test(form.cepDestino)) errs.cepDestino = 'CEP inválido (00000-000)';
    if (!form.peso || Number(form.peso) <= 0) errs.peso = 'Peso obrigatório (gramas)';
    if (!form.comprimento || Number(form.comprimento) <= 0) errs.comprimento = 'Obrigatório (cm)';
    if (!form.largura || Number(form.largura) <= 0) errs.largura = 'Obrigatório (cm)';
    if (!form.altura || Number(form.altura) <= 0) errs.altura = 'Obrigatório (cm)';
    return errs;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!endpoint) return;
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const requestBody = {
      cepOrigem: form.cepOrigem,
      cepDestino: form.cepDestino,
      peso: Number(form.peso),
      comprimento: Number(form.comprimento),
      largura: Number(form.largura),
      altura: Number(form.altura),
      vlDeclarado: form.vlDeclarado ? Number(form.vlDeclarado) : 0,
      servicosAdicionais: form.servicosAdicionais,
    };

    setLoading(true);
    setResultado(null);
    setDebugInfo(null);

    try {
      const { data } = await api.post(endpoint, requestBody);
      setResultado(data);
      setDebugInfo({
        method: 'POST',
        endpoint: `/api/v1${endpoint}`,
        request: requestBody,
        response: data,
      });
      if (!data.opcoes?.length) toast.error('Nenhuma opção disponível para esta rota');
      else toast.success(`${data.opcoes.length} opção(ões) encontrada(s)`);
    } catch (err) {
      const msg = extractApiError(err, 'Erro ao consultar transportadora');
      const errData = err.response?.data || { error: msg };
      setDebugInfo({
        method: 'POST',
        endpoint: `/api/v1${endpoint}`,
        request: requestBody,
        response: errData,
        isError: true,
      });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  function fmt(valor) {
    if (valor == null) return '—';
    return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }
  function fmtData(s) {
    if (!s) return '—';
    return new Date(s).toLocaleDateString('pt-BR');
  }

  return (
    <div>
      <div style={s.pageHeader}>
        <h1 style={s.pageTitle}>Simulador de Fretes</h1>
        <p style={s.pageDesc}>Teste a integração com as transportadoras ativas</p>
      </div>

      {/* Seletor de transportadora */}
      <div style={s.carrierBar}>
        {loadingCarriers ? (
          <span style={s.carrierLoading}>Carregando transportadoras...</span>
        ) : carriers.length === 0 ? (
          <span style={s.carrierLoading}>Nenhuma transportadora cadastrada</span>
        ) : (
          carriers.map((c) => (
            <button
              key={c.slug}
              onClick={() => { setSelectedSlug(c.slug); setResultado(null); setDebugInfo(null); }}
              style={{
                ...s.carrierBtn,
                ...(selectedSlug === c.slug ? s.carrierBtnActive : {}),
              }}
            >
              {c.name}
              {!CARRIER_ENDPOINTS[c.slug] && <span style={s.carrierTag}>em breve</span>}
            </button>
          ))
        )}
      </div>

      {/* Aviso se transportadora não tem endpoint de simulação */}
      {selected && !endpoint && (
        <div style={s.notImpl}>
          <span style={{ fontSize: '24px' }}>🚧</span>
          <div>
            <strong>Simulação não disponível para {selected.name}</strong>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#92400e' }}>
              A cotação direta para esta transportadora ainda não foi implementada no simulador.
            </p>
          </div>
        </div>
      )}

      {/* Formulário + Resultados */}
      {endpoint && (
        <div style={s.layout}>
          {/* Formulário */}
          <div style={s.formCard}>
            <h2 style={s.cardTitle}>📦 Dados do Envio</h2>
            <form onSubmit={handleSubmit} noValidate style={s.form}>
              <div style={ORIGEM_FIXA.includes(selectedSlug) ? undefined : s.row2}>
                {!ORIGEM_FIXA.includes(selectedSlug) && (
                  <Field label="CEP de Origem" error={errors.cepOrigem}>
                    <input style={inp(errors.cepOrigem)} value={form.cepOrigem} onChange={(e) => set('cepOrigem', e.target.value)} placeholder="00000-000" maxLength={9} />
                  </Field>
                )}
                <Field label="CEP de Destino" error={errors.cepDestino}>
                  <input style={inp(errors.cepDestino)} value={form.cepDestino} onChange={(e) => set('cepDestino', e.target.value)} placeholder="00000-000" maxLength={9} />
                </Field>
              </div>
              {ORIGEM_FIXA.includes(selectedSlug) && (
                <p style={{ fontSize: '12px', color: '#6b7280', margin: '-8px 0 0' }}>
                  📍 Origem fixa, configurada em Fretes &gt; Frete por Distância
                </p>
              )}

              <Field label="Peso (gramas)" error={errors.peso}>
                <input style={inp(errors.peso)} type="number" min="1" value={form.peso} onChange={(e) => set('peso', e.target.value)} placeholder="ex: 500" />
              </Field>

              <div style={s.row3}>
                <Field label="Comprimento (cm)" error={errors.comprimento}>
                  <input style={inp(errors.comprimento)} type="number" min="1" value={form.comprimento} onChange={(e) => set('comprimento', e.target.value)} placeholder="20" />
                </Field>
                <Field label="Largura (cm)" error={errors.largura}>
                  <input style={inp(errors.largura)} type="number" min="1" value={form.largura} onChange={(e) => set('largura', e.target.value)} placeholder="15" />
                </Field>
                <Field label="Altura (cm)" error={errors.altura}>
                  <input style={inp(errors.altura)} type="number" min="1" value={form.altura} onChange={(e) => set('altura', e.target.value)} placeholder="10" />
                </Field>
              </div>

              <Field label="Valor Declarado (R$) — opcional">
                <input style={inp()} type="number" min="0" step="0.01" value={form.vlDeclarado} onChange={(e) => set('vlDeclarado', e.target.value)} placeholder="0,00" />
              </Field>

              {selectedSlug === 'correios' && (
                <div>
                  <label style={s.label}>Serviços Adicionais</label>
                  <div style={s.checkboxGroup}>
                    {SERVICOS_ADICIONAIS.map((sv) => (
                      <label key={sv.cod} style={s.checkboxLabel}>
                        <input type="checkbox" checked={form.servicosAdicionais.includes(sv.cod)} onChange={() => toggleServico(sv.cod)} style={{ marginRight: '6px' }} />
                        {sv.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <button type="submit" disabled={loading} style={s.btnSubmit}>
                {loading ? '⏳ Consultando...' : `🔍 Simular — ${selected?.name}`}
              </button>
            </form>
          </div>

          {/* Resultados */}
          <div style={s.resultsPanel}>
            <h2 style={s.cardTitle}>📊 Opções de Frete</h2>

            {!resultado && !loading && !debugInfo && (
              <div style={s.emptyState}>
                <span style={s.emptyIcon}>📬</span>
                <p>Preencha os dados e clique em "Simular" para ver as opções disponíveis.</p>
              </div>
            )}

            {loading && (
              <div style={s.emptyState}>
                <span style={s.emptyIcon}>⏳</span>
                <p>Consultando {selected?.name}...</p>
              </div>
            )}

            {debugInfo?.isError && !loading && (
              <div style={s.errorBanner}>
                <div style={s.errorBannerTitle}>❌ Erro na requisição</div>
                <div style={s.errorBannerMsg}>{debugInfo.response?.error || debugInfo.response?.message || 'Erro desconhecido'}</div>
              </div>
            )}

            {resultado?.opcoes?.length > 0 && (
              <div style={s.opcoesList}>
                {resultado.opcoes.map((opcao, idx) => {
                  const temPreco = opcao.valorFrete != null;
                  const temPrazo = opcao.prazoEntrega != null;
                  const hasError = !temPreco || !!opcao.erro;
                  const isFirst = !hasError && idx === 0;
                  return (
                    <div key={idx} style={{ ...s.opcaoCard, ...(isFirst ? s.opcaoBest : {}) }}>
                      {isFirst && <div style={s.bestBadge}>Mais Barato</div>}
                      <div style={s.opcaoHeader}>
                        <div>
                          <div style={s.opcaoNome}>{opcao.descricao}</div>
                          <div style={s.opcaoCod}>Código: {opcao.codigoServico}</div>
                        </div>
                        <div style={s.opcaoPreco}>
                          {temPreco && !opcao.erro
                            ? fmt(opcao.valorFrete)
                            : <span style={{ color: '#dc2626', fontSize: '14px' }}>Indisponível</span>}
                        </div>
                      </div>
                      {hasError ? (
                        <div style={s.opcaoErro}>⚠️ {enriquecerErro(opcao.erro, opcao.codigoServico) || 'Preço não disponível para esta rota'}</div>
                      ) : (
                        <div style={s.opcaoDetails}>
                          <span style={s.opcaoDetail}>🕐 {temPrazo ? `${opcao.prazoEntrega} dia(s)` : '— dias'}</span>
                          <span style={s.opcaoDetail}>📅 Até: {fmtData(opcao.dataMaxima)}</span>
                          <span style={s.opcaoDetail}>🏠 {opcao.entregaDomiciliar === 'S' ? 'Domiciliar' : opcao.entregaDomiciliar === 'N' ? 'Sem domiciliar' : '—'}</span>
                          <span style={s.opcaoDetail}>📆 {opcao.entregaSabado === 'S' ? 'Entrega sábado' : 'Sem entrega sáb.'}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Debug — API response */}
      {debugInfo && !loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Card 1: contrato da API */}
          <div style={s.debugCard}>
            <button style={s.debugToggle} onClick={() => setDebugOpen((o) => !o)}>
              <div>
                <span style={s.debugMethod}>{debugInfo.method}</span>
                <span style={s.debugEndpoint}>{debugInfo.endpoint}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ ...s.debugStatus, color: debugInfo.isError ? '#f87171' : '#4ade80' }}>
                  {debugInfo.isError ? '● Erro' : '● 200 OK'}
                </span>
                <span style={s.debugChevron}>{debugOpen ? '▲' : '▼'}</span>
              </div>
            </button>
            {debugOpen && (
              <div style={s.debugPanels}>
                <div style={s.debugPanel}>
                  <div style={s.debugPanelTitle}>REQUEST — body enviado</div>
                  <pre style={s.debugJson}>{JSON.stringify(debugInfo.request, null, 2)}</pre>
                </div>
                <div style={s.debugPanel}>
                  <div style={s.debugPanelTitle}>RESPONSE — retorno da API</div>
                  <pre style={s.debugJson}>{JSON.stringify(debugInfo.response?.opcoes ?? debugInfo.response, null, 2)}</pre>
                </div>
              </div>
            )}
          </div>

          {/* Card 2: detalhes internos da integração */}
          {debugInfo.response?.debug && (
            <div style={s.debugCard}>
              <button style={s.debugToggle} onClick={() => setIntegrationOpen((o) => !o)}>
                <span style={s.debugEndpoint}>🔧 Detalhes da Integração</span>
                <span style={s.debugChevron}>{integrationOpen ? '▲' : '▼'}</span>
              </button>

              {integrationOpen && (
                <>
                  {/* Credenciais */}
                  <div style={s.debugSection}>
                    <div style={s.debugPanelTitle}>CREDENCIAIS UTILIZADAS</div>
                    <div style={s.credGrid}>
                      {Object.entries(debugInfo.response.debug.credentials || {}).map(([k, v]) => (
                        <div key={k} style={s.credRow}>
                          <span style={s.credKey}>{k}</span>
                          <span style={s.credVal}>{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Token (apenas Correios usa token de curta duração) */}
                  {(debugInfo.response.debug.token !== undefined || debugInfo.response.debug.tokenError) && (
                    <div style={s.debugSection}>
                      <div style={s.debugPanelTitle}>TOKEN GERADO</div>
                      {debugInfo.response.debug.tokenError ? (
                        <div style={{ padding: '12px 16px', color: '#f87171', fontSize: '13px' }}>
                          ❌ {debugInfo.response.debug.tokenError}
                        </div>
                      ) : (
                        <div style={{ padding: '12px 16px' }}>
                          <pre style={{ ...s.debugJson, padding: 0, background: 'transparent', color: '#4ade80', fontSize: '11px', wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                            {debugInfo.response.debug.token || '(nenhum)'}
                          </pre>
                          {debugInfo.response.debug.tokenExpiraEm && (
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px' }}>
                              Expira em: {new Date(debugInfo.response.debug.tokenExpiraEm).toLocaleString('pt-BR')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Raw — Correios expõe preços/prazos separados; demais transportadoras expõem `raw` único */}
                  {debugInfo.response.debug.rawPrecos !== undefined ? (
                    <div style={s.debugPanels}>
                      <div style={s.debugPanel}>
                        <div style={s.debugPanelTitle}>RAW — Preços</div>
                        <pre style={s.debugJson}>{JSON.stringify(debugInfo.response.debug.rawPrecos, null, 2)}</pre>
                      </div>
                      <div style={s.debugPanel}>
                        <div style={s.debugPanelTitle}>RAW — Prazos</div>
                        <pre style={s.debugJson}>{JSON.stringify(debugInfo.response.debug.rawPrazos, null, 2)}</pre>
                      </div>
                    </div>
                  ) : (
                    <div style={s.debugSection}>
                      <div style={s.debugPanelTitle}>RAW — Resposta da API</div>
                      <pre style={s.debugJson}>{JSON.stringify(debugInfo.response.debug.raw, null, 2)}</pre>
                    </div>
                  )}

                  {/* Logs do backend */}
                  {(() => {
                    const logs = debugInfo.response.debug.backendLogs;
                    if (!logs || !logs.length) return null;
                    return (
                      <div style={s.debugSection}>
                        <div style={s.debugPanelTitle}>LOGS DO BACKEND</div>
                        <pre style={s.debugJson}>
                          {logs.map((l) => `[${l.timestamp}] ${l.level.toUpperCase()}: ${l.message}`).join('\n')}
                        </pre>
                      </div>
                    );
                  })()}

                  {/* Erros HTTP brutos — Correios usa { preco, prazo }; demais usam array simples */}
                  {(() => {
                    const erros = debugInfo.response.debug.errosHttp;
                    if (!erros) return null;

                    if (Array.isArray(erros)) {
                      if (!erros.length) return null;
                      return (
                        <div style={s.debugSection}>
                          <div style={s.debugPanelTitle}>ERROS HTTP (resposta bruta da API)</div>
                          <pre style={{ ...s.debugJson, borderLeft: '3px solid #ef4444' }}>
                            {JSON.stringify(erros, null, 2)}
                          </pre>
                        </div>
                      );
                    }

                    const temErro = erros.preco?.length > 0 || erros.prazo?.length > 0;
                    if (!temErro) return null;
                    return (
                      <div style={s.debugSection}>
                        <div style={s.debugLabel}>Erros HTTP (resposta bruta da API)</div>
                        <div style={s.debugPanels}>
                          {erros.preco?.length > 0 && (
                            <div style={s.debugPanel}>
                              <div style={s.debugPanelTitle}>Erros — Preço</div>
                              <pre style={{ ...s.debugJson, borderLeft: '3px solid #ef4444' }}>
                                {JSON.stringify(erros.preco, null, 2)}
                              </pre>
                            </div>
                          )}
                          {erros.prazo?.length > 0 && (
                            <div style={s.debugPanel}>
                              <div style={s.debugPanelTitle}>Erros — Prazo</div>
                              <pre style={{ ...s.debugJson, borderLeft: '3px solid #ef4444' }}>
                                {JSON.stringify(erros.prazo, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
      <label style={s.label}>{label}</label>
      {children}
      {error && <span style={{ fontSize: '12px', color: '#dc2626' }}>{error}</span>}
    </div>
  );
}

function inp(error) {
  return {
    padding: '9px 12px',
    border: `1.5px solid ${error ? '#dc2626' : '#e5e7eb'}`,
    borderRadius: '7px',
    fontSize: '14px',
    background: error ? '#fef2f2' : '#f9fafb',
    fontFamily: 'inherit',
    color: '#111827',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };
}

const s = {
  pageHeader: { marginBottom: '20px' },
  pageTitle: { fontSize: '24px', fontWeight: '700', color: '#111827' },
  pageDesc: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },

  carrierBar: { display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' },
  carrierLoading: { fontSize: '14px', color: '#9ca3af' },
  carrierBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '8px 16px', borderRadius: '8px',
    border: '1.5px solid #e5e7eb', background: '#fff',
    fontSize: '14px', fontWeight: '500', color: '#374151',
    cursor: 'pointer', transition: 'all 0.15s', fontFamily: 'inherit',
  },
  carrierBtnActive: {
    background: '#eff6ff', borderColor: '#2563eb', color: '#1e40af',
  },
  carrierTag: {
    fontSize: '10px', background: '#fef3c7', color: '#92400e',
    padding: '1px 6px', borderRadius: '8px', fontWeight: '600',
  },

  notImpl: {
    display: 'flex', alignItems: 'flex-start', gap: '14px',
    background: '#fffbeb', border: '1.5px solid #fde68a',
    borderRadius: '10px', padding: '16px 20px', marginBottom: '20px',
    color: '#92400e',
  },

  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(340px, 420px) 1fr',
    gap: '20px', alignItems: 'start', marginBottom: '20px',
  },
  formCard: {
    background: '#fff', borderRadius: '12px', padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f3f4f6',
  },
  resultsPanel: {
    background: '#fff', borderRadius: '12px', padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f3f4f6', minHeight: '300px',
  },
  cardTitle: { fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '20px' },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  row3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' },
  label: { fontSize: '13px', fontWeight: '500', color: '#374151' },
  checkboxGroup: { display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' },
  checkboxLabel: { fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', cursor: 'pointer' },
  btnSubmit: {
    padding: '11px', background: 'linear-gradient(135deg, #1e40af, #2563eb)',
    color: '#fff', border: 'none', borderRadius: '8px',
    fontSize: '14px', fontWeight: '600', cursor: 'pointer',
    marginTop: '4px', fontFamily: 'inherit',
  },

  emptyState: { textAlign: 'center', padding: '48px 24px', color: '#9ca3af' },
  emptyIcon: { fontSize: '40px', display: 'block', marginBottom: '12px' },

  errorBanner: {
    background: '#fef2f2', border: '1.5px solid #fecaca',
    borderRadius: '10px', padding: '16px', marginBottom: '12px',
  },
  errorBannerTitle: { fontSize: '14px', fontWeight: '700', color: '#b91c1c', marginBottom: '6px' },
  errorBannerMsg: { fontSize: '13px', color: '#dc2626', lineHeight: '1.5' },

  opcoesList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  opcaoCard: { border: '1.5px solid #e5e7eb', borderRadius: '10px', padding: '16px', position: 'relative' },
  opcaoBest: { borderColor: '#2563eb', background: '#eff6ff' },
  bestBadge: {
    position: 'absolute', top: '-10px', right: '12px',
    background: '#2563eb', color: '#fff', fontSize: '11px',
    fontWeight: '700', padding: '2px 10px', borderRadius: '10px',
    textTransform: 'uppercase', letterSpacing: '0.5px',
  },
  opcaoHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' },
  opcaoNome: { fontSize: '15px', fontWeight: '600', color: '#111827' },
  opcaoCod: { fontSize: '12px', color: '#9ca3af', marginTop: '2px' },
  opcaoPreco: { fontSize: '20px', fontWeight: '700', color: '#1e40af' },
  opcaoDetails: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  opcaoDetail: {
    fontSize: '12px', color: '#6b7280', background: '#f9fafb',
    padding: '3px 8px', borderRadius: '6px', border: '1px solid #e5e7eb',
  },
  opcaoErro: { marginTop: '8px', fontSize: '12px', color: '#dc2626', background: '#fef2f2', padding: '6px 10px', borderRadius: '6px' },

  debugCard: {
    background: '#0f172a', borderRadius: '12px',
    border: '1px solid #1e293b', overflow: 'hidden',
  },
  debugToggle: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    width: '100%', padding: '14px 20px',
    background: 'none', border: 'none', borderBottom: '1px solid #1e293b',
    cursor: 'pointer', gap: '12px', textAlign: 'left',
  },
  debugChevron: { fontSize: '10px', color: '#475569', flexShrink: 0 },
  debugMethod: {
    fontSize: '12px', fontWeight: '700', background: '#1d4ed8',
    color: '#fff', padding: '3px 8px', borderRadius: '5px', marginRight: '10px',
  },
  debugEndpoint: { fontSize: '14px', fontWeight: '500', color: '#94a3b8', fontFamily: 'monospace' },
  debugStatus: { fontSize: '12px', fontWeight: '600', flexShrink: 0 },
  debugPanels: {
    display: 'grid', gridTemplateColumns: '1fr 1fr',
  },
  debugPanel: { borderRight: '1px solid #1e293b' },
  debugPanelTitle: {
    fontSize: '11px', fontWeight: '700', color: '#475569',
    textTransform: 'uppercase', letterSpacing: '1px',
    padding: '10px 16px', borderBottom: '1px solid #1e293b',
  },
  debugJson: {
    margin: 0, padding: '16px',
    background: '#020617', color: '#7dd3fc',
    fontSize: '12px', lineHeight: '1.6',
    overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
  },
  debugSection: { borderBottom: '1px solid #1e293b' },
  credGrid: { display: 'flex', flexDirection: 'column', gap: '4px', padding: '12px 16px' },
  credRow: { display: 'flex', gap: '12px', fontSize: '12px' },
  credKey: { color: '#64748b', minWidth: '160px', fontFamily: 'monospace' },
  credVal: { color: '#e2e8f0', fontFamily: 'monospace', wordBreak: 'break-all' },
};
